<template>
  <UInput
    ref="input"
    v-bind="domAttributes"
    :model-value="displayValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :autocomplete="autocomplete"
    :class="rootClass"
    :ui="inputUi"
    @update:model-value="onInput"
    @blur="handleBlur"
  />
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'
import UInput from '@nuxt/ui/components/Input.vue'

import { useCurrencyInput } from '~/composables/useCurrencyInput'
import { fieldControlInput } from '~/design-system/base/fieldControlInput'
import { CurrencyCode } from '~/enums/CurrencyCode'

const props = withDefaults(
  defineProps<{
    /** Controlled unformatted monetary value; a blank string means no value. */
    modelValue: number | string | null
    /** ISO 4217 currency code; it sets the symbol, separators and fraction digits. */
    currency?: CurrencyCode
    /** Hint displayed when the input has no value. */
    placeholder?: string
    /** Whether user interaction with the input is disabled. */
    disabled?: boolean
    /** Whether the input value can be viewed but not edited. */
    readonly?: boolean
    /** HTML identifier assigned to the input element. */
    id?: string | undefined
    /** HTML form field name assigned to the input element. */
    name?: string | undefined
    /** HTML autocomplete hint assigned to the input element. */
    autocomplete?: string
    /** CSS classes applied to the input wrapper. */
    wrapperClass?: string
    /** Paints the error state, the way every other Nuxt UI control does. */
    invalid?: boolean
    /** Horizontal alignment applied to the input text. */
    textAlign?: 'left' | 'center' | 'right'
  }>(),
  {
    autocomplete: 'off',
    currency: CurrencyCode.USD,
    id: undefined,
    invalid: false,
    name: undefined,
    placeholder: '$0.00',
    textAlign: 'center',
    wrapperClass: ''
  }
)

const emit = defineEmits<{
  (_event: 'update:modelValue', _value: number | null): void
  (_event: 'blurred' | 'focused'): void
}>()

const input = useTemplateRef<{ inputRef?: HTMLInputElement | null }>('input')
const element = computed(() => input.value?.inputRef)

const { displayValue, onBeforeInput, onBlur, onFocus, onInput } = useCurrencyInput({
  currency: () => props.currency,
  element,
  emitUpdate: (value) => {
    emit('update:modelValue', value)
  },
  modelValue: () => props.modelValue
})

const alignmentClass = computed(() => {
  if (props.textAlign === 'left') return 'text-left'
  if (props.textAlign === 'right') return 'text-right'
  return 'text-center'
})

const inputUi = computed(() => fieldControlInput.ui(props.invalid, alignmentClass.value))

// UInput's root is inline-flex and shrinks to content; wrapperClass comes last so it still wins.
const rootClass = computed(() => ['w-full', props.wrapperClass])

const onKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Enter') return
  event.preventDefault()
  if (event.target instanceof HTMLInputElement) event.target.blur()
}

// inputmode and these DOM listeners aren't declared UInput props, so they ride as fall-through attrs.
const domAttributes = computed(() => ({
  ...fieldControlInput.props,
  inputmode: 'decimal',
  onBeforeinput: onBeforeInput,
  onFocus: () => {
    onFocus()
    emit('focused')
  },
  onKeydown,
  type: 'text',
  ...(props.id === undefined ? {} : { id: props.id }),
  ...(props.name === undefined ? {} : { name: props.name })
}))

const handleBlur = () => {
  onBlur()
  emit('blurred')
}
</script>
